import { useMemo } from 'react';
import { getGamesByCategory, getLatestGames, type Game } from '@lib/content';
import { GAME_LIMITS } from '../config';

export interface GameRecommendations {
  sidebarGames: Game[];
  moreGames: Game[];
}

/**
 * Get series/version games for current game
 */
function getSeriesGames(currentGame: Game, allCategoryGames: Game[]): Game[] {
  const currentTitle = currentGame.title.toLowerCase();
  const currentSlug = currentGame.slug.toLowerCase();

  // Extract base name from title (remove numbers and common words)
  const getBaseName = (title: string) => {
    return title
      .toLowerCase()
      .replace(/\b(1|2|3|4|5|6|7|8|9|10|i+|ii+|iii+|iv+|v+)\b/g, '')
      .replace(/\b(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)\b/g, '')
      .replace(/[-:\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  const getBaseSlug = (slug: string) => {
    return slug
      .replace(/-(\d+)$/, '') // Remove trailing numbers
      .replace(/-(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)(-\d+)?$/, '')
      .replace(/-(1|2|3|4|5|6|7|8|9|10)(-\w+)*$/, '');
  };

  const currentBaseName = getBaseName(currentGame.title);
  const currentBaseSlug = getBaseSlug(currentGame.slug);

  // Find series games
  const seriesGames = allCategoryGames.filter(game => {
    if (game.slug === currentGame.slug) return false;

    const gameBaseName = getBaseName(game.title);
    const gameBaseSlug = getBaseSlug(game.slug);

    // Check if same series by title or slug
    return (currentBaseName === gameBaseName && currentBaseName.length > 3) ||
      (currentBaseSlug === gameBaseSlug && currentBaseSlug.length > 3) ||
      // Manual series mappings for common cases
      isInSameSeries(currentGame.slug, game.slug);
  });

  // Sort by version/number (try to get sequential order)
  return seriesGames.sort((a, b) => {
    const aNum = extractNumber(a.title) || extractNumber(a.slug) || 0;
    const bNum = extractNumber(b.title) || extractNumber(b.slug) || 0;
    return aNum - bNum;
  });
}

/**
 * Extract number from title/slug for version ordering
 */
function extractNumber(text: string): number | null {
  const match = text.match(/\b(\d+)\b/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Manual series detection for common game series
 */
function isInSameSeries(slug1: string, slug2: string): boolean {
  const seriesMap: Record<string, string[]> = {
    // 2048 Series
    '2048': ['2048-fusion', '2048-multitask', '2048-suika', 'doge-2048', 'meme-2048'],
    '2048-fusion': ['2048', '2048-multitask', '2048-suika', 'doge-2048', 'meme-2048'],
    '2048-multitask': ['2048', '2048-fusion', '2048-suika', 'doge-2048', 'meme-2048'],
    '2048-suika': ['2048', '2048-fusion', '2048-multitask', 'doge-2048', 'meme-2048'],
    'doge-2048': ['2048', '2048-fusion', '2048-multitask', '2048-suika', 'meme-2048'],
    'meme-2048': ['2048', '2048-fusion', '2048-multitask', '2048-suika', 'doge-2048'],

    // Block Games Series  
    'block-blast': ['1010-deluxe', 'wood-block-puzzle', 'blockudoku', 'element-blocks', 'tetris'],
    'block-breaker': ['block-blast', 'block-the-pig', 'blockdrop', 'blockudoku', 'element-blocks'],
    'block-the-pig': ['block-blast', 'block-breaker', 'blockdrop', 'blockudoku', 'element-blocks'],
    'blockdrop': ['block-blast', 'block-breaker', 'block-the-pig', 'blockudoku', 'element-blocks'],
    'blockudoku': ['block-blast', 'block-breaker', 'block-the-pig', 'blockdrop', 'element-blocks'],
    'element-blocks': ['block-blast', 'block-breaker', 'block-the-pig', 'blockdrop', 'blockudoku'],

    // Bloons TD Series
    'bloons-td': ['bloons-td-2', 'bloons-td-4'],
    'bloons-td-2': ['bloons-td', 'bloons-td-4'],
    'bloons-td-4': ['bloons-td', 'bloons-td-2'],

    // Bottle Games Series
    'bottle-flip': ['bottle-flip-3d', 'bottle-jump-3d'],
    'bottle-flip-3d': ['bottle-flip', 'bottle-jump-3d'],
    'bottle-jump-3d': ['bottle-flip', 'bottle-flip-3d'],

    // Bubble Games Series
    'bubble-pop-adventures': ['bubble-pop-star', 'bubble-shooter', 'bubble-tower-3d'],
    'bubble-pop-star': ['bubble-pop-adventures', 'bubble-shooter', 'bubble-tower-3d'],
    'bubble-shooter': ['bubble-pop-adventures', 'bubble-pop-star', 'bubble-tower-3d'],
    'bubble-tower-3d': ['bubble-pop-adventures', 'bubble-pop-star', 'bubble-shooter'],

    // Circle Games
    'circlo': ['circloo-2'],
    'circloo-2': ['circlo'],

    // Connect Games Series
    'connect-3': ['onet-connect-classic'],
    'onet-connect-classic': ['connect-3'],

    // Color Games Series
    '1010-color-match': ['color-match', 'color-burst-3d', 'color-page-asmr', 'color-pixel-art', 'color-water-sort-3d'],
    'color-burst-3d': ['1010-color-match', 'color-match', 'color-page-asmr', 'color-pixel-art', 'color-water-sort-3d'],
    'color-match': ['1010-color-match', 'color-burst-3d', 'color-page-asmr', 'color-pixel-art', 'color-water-sort-3d'],
    'color-page-asmr': ['1010-color-match', 'color-burst-3d', 'color-match', 'color-pixel-art', 'color-water-sort-3d'],
    'color-pixel-art': ['1010-color-match', 'color-burst-3d', 'color-match', 'color-page-asmr', 'color-water-sort-3d'],
    'color-water-sort-3d': ['1010-color-match', 'color-burst-3d', 'color-match', 'color-page-asmr', 'color-pixel-art'],

    // Cut the Rope Series
    'cut-the-rope': ['cut-the-rope-holiday', 'cut-the-rope-time-travel'],
    'cut-the-rope-holiday': ['cut-the-rope', 'cut-the-rope-time-travel'],
    'cut-the-rope-time-travel': ['cut-the-rope', 'cut-the-rope-holiday'],

    // Dash Games Series
    'electron-dash': ['geometry-dash-classic', 'geometry-dash-scratch', 'glitch-dash', 'stacky-dash'],
    'geometry-dash-classic': ['electron-dash', 'geometry-dash-scratch', 'glitch-dash', 'stacky-dash'],
    'geometry-dash-scratch': ['electron-dash', 'geometry-dash-classic', 'glitch-dash', 'stacky-dash'],
    'glitch-dash': ['electron-dash', 'geometry-dash-classic', 'geometry-dash-scratch', 'stacky-dash'],
    'stacky-dash': ['electron-dash', 'geometry-dash-classic', 'geometry-dash-scratch', 'glitch-dash'],

    // Jewels Series
    'jewel-shuffle': ['jewels-blitz-5', 'jewels-planet'],
    'jewels-blitz-5': ['jewel-shuffle', 'jewels-planet'],
    'jewels-planet': ['jewel-shuffle', 'jewels-blitz-5'],

    // Mahjongg Series
    'mahjongg-dimensions': ['mahjongg-solitaire'],
    'mahjongg-solitaire': ['mahjongg-dimensions'],

    // Merge Games Series
    'merge-cakes': ['merge-fruits', 'merge-heroes', 'merge-jewels', 'merge-master', 'merge-pirates', 'merge-round-racers'],
    'merge-fruits': ['merge-cakes', 'merge-heroes', 'merge-jewels', 'merge-master', 'merge-pirates', 'merge-round-racers'],
    'merge-heroes': ['merge-cakes', 'merge-fruits', 'merge-jewels', 'merge-master', 'merge-pirates', 'merge-round-racers'],
    'merge-jewels': ['merge-cakes', 'merge-fruits', 'merge-heroes', 'merge-master', 'merge-pirates', 'merge-round-racers'],
    'merge-master': ['merge-cakes', 'merge-fruits', 'merge-heroes', 'merge-jewels', 'merge-pirates', 'merge-round-racers'],
    'merge-pirates': ['merge-cakes', 'merge-fruits', 'merge-heroes', 'merge-jewels', 'merge-master', 'merge-round-racers'],
    'merge-round-racers': ['merge-cakes', 'merge-fruits', 'merge-heroes', 'merge-jewels', 'merge-master', 'merge-pirates'],

    // Minecraft Series
    'minecraft-15': ['minecraft-18', 'minecraft-case-simulator', 'minecraft-classic', 'paper-minecraft'],
    'minecraft-18': ['minecraft-15', 'minecraft-case-simulator', 'minecraft-classic', 'paper-minecraft'],
    'minecraft-case-simulator': ['minecraft-15', 'minecraft-18', 'minecraft-classic', 'paper-minecraft'],
    'minecraft-classic': ['minecraft-15', 'minecraft-18', 'minecraft-case-simulator', 'paper-minecraft'],
    'paper-minecraft': ['minecraft-15', 'minecraft-18', 'minecraft-case-simulator', 'minecraft-classic'],

    // Riddle School Series
    'riddle-school-1': ['riddle-school-2', 'riddle-school-3', 'riddle-school-4', 'riddle-school-5'],
    'riddle-school-2': ['riddle-school-1', 'riddle-school-3', 'riddle-school-4', 'riddle-school-5'],
    'riddle-school-3': ['riddle-school-1', 'riddle-school-2', 'riddle-school-4', 'riddle-school-5'],
    'riddle-school-4': ['riddle-school-1', 'riddle-school-2', 'riddle-school-3', 'riddle-school-5'],
    'riddle-school-5': ['riddle-school-1', 'riddle-school-2', 'riddle-school-3', 'riddle-school-4'],

    // Riddle School Transfer Series
    'riddle-school-transfer-1': ['riddle-school-transfer-2'],
    'riddle-school-transfer-2': ['riddle-school-transfer-1'],

    // Solitaire Series
    'solitaire': ['spider-solitaire', 'freecell-solitaire', 'daily-solitaire'],
    'spider-solitaire': ['solitaire', 'freecell-solitaire', 'daily-solitaire'],
    'freecell-solitaire': ['solitaire', 'spider-solitaire', 'daily-solitaire'],
    'daily-solitaire': ['solitaire', 'spider-solitaire', 'freecell-solitaire'],

    // Ball Sort Games Series
    'ball-sort-halloween': ['ball-sort-puzzle', 'ball-sort-soccer'],
    'ball-sort-puzzle': ['ball-sort-halloween', 'ball-sort-soccer'],
    'ball-sort-soccer': ['ball-sort-halloween', 'ball-sort-puzzle'],

    // Stack Games Series
    'stack': ['stack-bounce', 'stack-bump-3d'],
    'stack-bounce': ['stack', 'stack-bump-3d'],
    'stack-bump-3d': ['stack', 'stack-bounce'],

    // Tetris Games
    'tetris': ['flash-tetris', 'hextris'],
    'flash-tetris': ['tetris', 'hextris'],
    'hextris': ['tetris', 'flash-tetris'],

    // Watermelon/Suika Games
    'suika-game': ['watermelon-game'],
    'watermelon-game': ['suika-game'],

    // Drawing Games
    'draw-climber': ['draw-the-hill', 'draw-the-line'],
    'draw-the-hill': ['draw-climber', 'draw-the-line'],
    'draw-the-line': ['draw-climber', 'draw-the-hill'],

    // Mining Games
    'mine-blocks': ['minecraft-15', 'minecraft-18', 'minecraft-classic', 'paper-minecraft'],
    'craftmine': ['minecraft-15', 'minecraft-18', 'minecraft-classic', 'paper-minecraft']
  };

  return seriesMap[slug1]?.includes(slug2) ?? false;
}

/**
 * Enhanced game recommendations with series/version priority
 * Priority: Featured games > Series games > Same category games > Latest games
 */
export function useGameRecommendations(game: Game | null): GameRecommendations {
  return useMemo(() => {
    if (!game) return { sidebarGames: [], moreGames: [] };

    // Define priority games for homepage sidebar
    const priorityGames = [
      '2048',
      '1010-color-match',
      '2048-fusion',
      '1010-deluxe',
      'merge-cakes',
      'watermelon-game',
      'merge-fruits',
      'block-breaker',
      'block-blast',
      'suika-game',
      'tetris',
      'bubble-shooter',
      'cut-the-rope',
      'minecraft-classic',
      'solitaire',
      'emoji-puzzle',
      'color-match'
    ];

    // Get all games from all categories of current game
    const allCategories = game.categories || [];
    const allRelatedGames: Game[] = [];
    const usedSlugs = new Set<string>([game.slug]);

    // Collect games from all categories (not just primary)
    for (const category of allCategories) {
      const categoryGames = getGamesByCategory(category).filter((g) => !usedSlugs.has(g.slug));
      allRelatedGames.push(...categoryGames);
      categoryGames.forEach(g => usedSlugs.add(g.slug));
    }

    // PRIORITY 1: Get series/version games first
    const seriesGames = getSeriesGames(game, allRelatedGames);

    // PRIORITY 2: Get priority games from the current game's categories
    const priorityRelatedGames = allRelatedGames.filter(g =>
      priorityGames.includes(g.slug) && !seriesGames.some(sg => sg.slug === g.slug)
    ).sort((a, b) => {
      const aIndex = priorityGames.indexOf(a.slug);
      const bIndex = priorityGames.indexOf(b.slug);
      return aIndex - bIndex;
    });

    // PRIORITY 3: Get remaining category games (excluding series and priority games)
    const remainingCategoryGames = allRelatedGames.filter(g =>
      !seriesGames.some(sg => sg.slug === g.slug) &&
      !priorityRelatedGames.some(pg => pg.slug === g.slug)
    );

    // PRIORITY 4: Get latest games as fallback
    const latestGames = getLatestGames(GAME_LIMITS.LATEST_GAMES_POOL)
      .filter((g) => g.slug !== game.slug && !usedSlugs.has(g.slug));

    // Build sidebar with priority order
    const sidebar: Game[] = [];
    const finalUsedSlugs = new Set<string>([game.slug]);

    // Add series games first (highest priority for related games)
    for (const seriesGame of seriesGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        sidebar.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add priority games from same categories
    for (const priorityGame of priorityRelatedGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        sidebar.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        sidebar.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        sidebar.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    // Build more games section
    const more: Game[] = [];

    // Add remaining series games that didn't fit in sidebar
    for (const seriesGame of seriesGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        more.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add remaining priority games
    for (const priorityGame of priorityRelatedGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        more.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        more.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        more.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    return { sidebarGames: sidebar, moreGames: more };
  }, [game]);
}
